import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { ChartLineUp, Buildings, CheckCircle, Warning, X, ArrowCircleDown, Package } from "@phosphor-icons/react";

// Indian number formatter: ₹1.42 Cr, ₹83.3 L, ₹48,500
function inr(n) {
  if (n === null || n === undefined) return "-";
  n = Number(n);
  const sign = n < 0 ? "-" : "";
  n = Math.abs(n);
  if (n >= 1e7) return `${sign}₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${sign}₹${(n / 1e5).toFixed(2)} L`;
  return `${sign}₹${Math.round(n).toLocaleString("en-IN")}`;
}

function pct(x) { return `${(Number(x || 0) * 100).toFixed(1)}%`; }

function Chip({ color, label, children, testId }) {
  const map = {
    green: "bg-[#0D9488]/10 border-[#0D9488] text-[#0D9488]",
    amber: "bg-[#CA8A04]/10 border-[#CA8A04] text-[#CA8A04]",
    red:   "bg-[#D90429]/10 border-[#D90429] text-[#D90429]",
    gray:  "bg-black/5 border-black/20 text-[#717680]",
  };
  const Icon = color === "red" ? Warning : color === "amber" ? Warning : color === "green" ? CheckCircle : ArrowCircleDown;
  return (
    <div data-testid={testId} className={`border px-2.5 py-1.5 text-[11px] flex items-center gap-1.5 ${map[color] || map.gray}`}>
      <Icon size={13} weight="fill" />
      <span className="uppercase font-mono-shelv tracking-widest font-bold">{label}</span>
      <span className="text-[#0A0A0A]">· {children}</span>
    </div>
  );
}

function Tile({ title, value, subtitle, source, testId }) {
  return (
    <div className="bg-white border border-black/10 p-4" data-testid={testId}>
      <div className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]">{title}</div>
      <div className="text-2xl md:text-3xl font-bold font-display mt-1 break-all">{value}</div>
      {subtitle && <div className="text-[11px] text-[#717680] mt-0.5">{subtitle}</div>}
      {source && <div className="text-[10px] text-[#717680]/70 mt-1 font-mono-shelv">source · {source}</div>}
    </div>
  );
}

export default function Snapshot() {
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");
  const [fy, setFy] = useState("");
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/finance/snapshot/companies");
        setCompanies(data.companies || []);
        if (data.companies?.[0]) {
          setCompany(data.companies[0].company_code);
          setFy((data.companies[0].fys || []).slice(-1)[0] || "");
        }
      } catch (_) { toast.error("Failed to load companies"); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!company || !fy) return;
    setLoading(true);
    api.get(`/finance/snapshot?company=${company}&fy=${fy}&as_of=${asOf}`)
      .then(({ data }) => setSnap(data))
      .catch(() => toast.error("Failed to load snapshot"))
      .finally(() => setLoading(false));
  }, [company, fy, asOf]);

  const maxSeries = useMemo(() => {
    if (!snap) return 1;
    let m = 0;
    Object.values(snap.series || {}).forEach(x => {
      m = Math.max(m, x.sales, x.purchases, x.collections);
    });
    return m || 1;
  }, [snap]);

  if (loading && !snap) return <Layout><div className="p-6 text-sm text-[#717680]">Loading snapshot...</div></Layout>;
  if (companies.length === 0) return <Layout><div className="p-6"><div className="bg-white border border-black/10 p-6 text-sm text-[#717680]">No BUSY data yet. Set up the sync agent in <b>Finance → Sync Settings</b> first.</div></div></Layout>;
  if (!snap) return <Layout><div className="p-6 text-sm text-[#717680]">No data</div></Layout>;

  const h = snap.health || {};
  const healthChips = [
    {
      color: h.days_since_entry == null ? "gray" : h.days_since_entry > 2 ? "red" : h.days_since_entry > 0 ? "amber" : "green",
      label: "Last entry", text: h.days_since_entry == null ? "never" : `${h.days_since_entry}d ago`,
    },
    {
      color: h.days_since_recon == null ? "amber" : h.days_since_recon > 7 ? "red" : "green",
      label: "Bank recon",
      text: h.days_since_recon == null ? "no recon yet" : `${h.days_since_recon}d · ${h.unrec_count} unrec · ${inr(h.unrec_value)}`,
    },
    { color: h.suspense > 0 ? "red" : "green", label: "Suspense", text: inr(h.suspense) },
    { color: (h.backdated || []).length > 0 ? "amber" : "green", label: "Backdated edits", text: `${(h.backdated || []).length} vch 7d+` },
    { color: h.late_entered > 0 ? "amber" : "green", label: "Late purchases", text: `${h.late_entered} ≥3d` },
    { color: h.cancelled > 0 ? "amber" : "green", label: "Cancelled", text: `${h.cancelled}` },
    {
      color: !h.agent_last_seen ? "red" : ((Date.now() - new Date(h.agent_last_seen).getTime()) > 3600_000) ? "red" : "green",
      label: "Agent", text: h.agent_last_seen ? new Date(h.agent_last_seen).toLocaleString("en-IN") : "never",
    },
  ];

  return (
    <Layout>
      <div className="px-4 md:px-6 py-6 min-h-screen bg-[#F5F3EF]">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Finance Snapshot</h1>
            <div className="mt-1 text-[11px] text-[#717680]">
              Data as of <b>{snap.as_of}</b> · last BUSY entry <b>{h.last_entry ? new Date(h.last_entry).toLocaleString("en-IN") : "-"}</b> · agent <b className={h.agent_last_seen && ((Date.now() - new Date(h.agent_last_seen).getTime()) < 3600_000) ? "text-[#0D9488]" : "text-[#D90429]"}>{h.agent_last_seen ? "live" : "offline"}</b>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <select data-testid="snap-company-select" value={company} onChange={e => setCompany(e.target.value)} className="border border-black/15 px-3 py-2 text-sm">
              {companies.map(c => <option key={c.company_code} value={c.company_code}>{c.Name || c.company_code}</option>)}
            </select>
            <select data-testid="snap-fy-select" value={fy} onChange={e => setFy(e.target.value)} className="border border-black/15 px-3 py-2 text-sm font-mono-shelv">
              {((companies.find(c => c.company_code === company) || {}).fys || []).map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <input type="date" data-testid="snap-asof" value={asOf} onChange={e => setAsOf(e.target.value)} className="border border-black/15 px-3 py-2 text-sm" />
          </div>
        </div>

        {/* 1. Today's brief */}
        <section className="bg-[#0A0A0A] text-white p-5 mb-5" data-testid="snap-brief">
          <div className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#D90429] mb-2">Today's brief · template (AI brief lands here in Prompt 3)</div>
          <ul className="space-y-1.5">
            {(snap.brief || []).map((line, i) => <li key={i} className="text-sm leading-relaxed">· {line}</li>)}
          </ul>
        </section>

        {/* 2. Eight tiles */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="snap-tiles">
          <Tile testId="tile-sales" title="Sales this month"
                value={inr(snap.sales.mtd)}
                subtitle={`last: ${inr(snap.sales.last_month)} · YTD ${inr(snap.sales.ytd)}`}
                source="Tran2 · sales ledger" />
          <Tile testId="tile-collections" title="Collections this month"
                value={inr(snap.collections.mtd)}
                subtitle={`last: ${inr(snap.collections.last_month)} · YTD ${inr(snap.collections.ytd)}`}
                source="Tran1 · debtor group · VchType 14" />
          <Tile testId="tile-margin" title="Gross margin YTD"
                value={snap.margin.ytd_pct === null ? "n/a" : pct(snap.margin.ytd_pct)}
                subtitle={snap.margin.ytd_pct === null
                  ? `cost known for ${pct(snap.margin.ytd_coverage || 0)} of sales (need \u226580%)`
                  : `MTD ${snap.margin.mtd_pct === null ? "n/a" : pct(snap.margin.mtd_pct)} \u00B7 amount ${inr(snap.margin.ytd_amount)}`}
                source="RecType=2 · abs(Value3)" />
          <Tile testId="tile-bank" title="Bank CC used"
                value={inr(snap.bank.cc_used)}
                subtitle={snap.bank.cc_limit ? `of ${inr(snap.bank.cc_limit)} · headroom ${inr(snap.bank.headroom)}` : "limit not set in Sync Settings"}
                source={snap.bank.cc_name || "ParentGrp 128"} />
          <Tile testId="tile-recv" title="Receivables"
                value={inr(snap.receivables.total)}
                subtitle={`${snap.receivables.bills} bills · overdue ${inr(snap.receivables.overdue)}${(snap.receivables.advances_total || 0) > 0 ? ` · advances ${inr(snap.receivables.advances_total)}` : ""}`}
                source="Tran3 · group 116" />
          <Tile testId="tile-pay" title="Supplier bills due/overdue in 7d"
                value={inr(snap.payables.due7_total)}
                subtitle={`${(snap.payables.due7 || []).length} bills · total payables ${inr(snap.payables.total)}${(snap.payables.advances_total || 0) > 0 ? ` · advances paid ${inr(snap.payables.advances_total)}` : ""}`}
                source="Tran3 · group 117" />
          <Tile testId="tile-gst" title={`GST for ${snap.prev_month}`}
                value={inr(Math.abs(snap.gst.prev.net || 0))}
                subtitle={`${snap.gst.prev.label === "credit_carried_forward" ? "credit c/f" : "payable (due 20th)"} \u00B7 out ${inr(snap.gst.prev.out)} \u2212 input ${inr(snap.gst.prev.inp)}`}
                source="group 118 · Output/Input match" />
          <Tile testId="tile-stock" title="Stock value (WAC)"
                value={inr(snap.stock.value)}
                subtitle={`${(snap.stock.slow || []).length} items unsold 90d+`}
                source="RecType=2 · weighted avg" />
        </section>

        {/* 3. Health chips */}
        <section className="flex flex-wrap gap-2 mb-5" data-testid="snap-health">
          {healthChips.map((c, i) => <Chip key={i} color={c.color} label={c.label} testId={`health-chip-${i}`}>{c.text}</Chip>)}
        </section>

        {/* 4. Month-by-month bar chart */}
        <section className="bg-white border border-black/10 p-5 mb-5" data-testid="snap-chart">
          <h3 className="font-display text-sm font-bold uppercase font-mono-shelv tracking-widest text-[#717680] mb-3">Month-by-month · sales · purchases · collections</h3>
          <div className="overflow-x-auto">
            <div className="flex gap-2 min-w-full items-end" style={{ height: 180 }}>
              {(snap.months || []).map(m => {
                const row = snap.series[m] || { sales: 0, purchases: 0, collections: 0 };
                const h1 = Math.max(2, (Math.abs(row.sales) / maxSeries) * 150);
                const h2 = Math.max(2, (Math.abs(row.purchases) / maxSeries) * 150);
                const h3 = Math.max(2, (Math.abs(row.collections) / maxSeries) * 150);
                return (
                  <div key={m} className="flex-shrink-0 flex flex-col items-center" style={{ width: 56 }}>
                    <div className="flex gap-0.5 items-end" style={{ height: 150 }}>
                      <div className="w-3 bg-[#D90429]" style={{ height: h1 }} title={`sales ${inr(row.sales)}`} />
                      <div className="w-3 bg-[#CA8A04]" style={{ height: h2 }} title={`purchases ${inr(row.purchases)}`} />
                      <div className="w-3 bg-[#0D9488]" style={{ height: h3 }} title={`collections ${inr(row.collections)}`} />
                    </div>
                    <div className="text-[9px] font-mono-shelv text-[#717680] mt-1">{m.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex gap-4 mt-3 text-[10px] font-mono-shelv uppercase tracking-widest">
            <span><span className="inline-block w-3 h-2 bg-[#D90429] mr-1" />Sales</span>
            <span><span className="inline-block w-3 h-2 bg-[#CA8A04] mr-1" />Purchases</span>
            <span><span className="inline-block w-3 h-2 bg-[#0D9488] mr-1" />Collections</span>
          </div>
        </section>

        {/* 5. Receivables ageing + top overdue */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <div className="bg-white border border-black/10 p-5" data-testid="snap-ageing">
            <h3 className="font-display text-sm font-bold uppercase font-mono-shelv tracking-widest text-[#717680] mb-3">Receivables ageing</h3>
            <div className="space-y-2">
              {(snap.receivables.buckets || []).map(b => {
                const pctW = snap.receivables.total ? Math.max(0, (b.amt / snap.receivables.total) * 100) : 0;
                return (
                  <div key={b.label}>
                    <div className="flex justify-between text-xs"><span className="font-mono-shelv">{b.label}d</span><span>{inr(b.amt)}</span></div>
                    <div className="h-2 bg-black/5 mt-1"><div className="h-full bg-[#D90429]" style={{ width: `${pctW}%` }} /></div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-[#717680]/70 mt-3 font-mono-shelv">source · bill-wise by bill date, Tran3</div>
          </div>
          <div className="bg-white border border-black/10 p-5" data-testid="snap-top-overdue">
            <h3 className="font-display text-sm font-bold uppercase font-mono-shelv tracking-widest text-[#717680] mb-3">Top overdue customers</h3>
            {(snap.receivables.top_overdue || []).length === 0 ? <div className="text-sm text-[#717680] italic">No overdue customers 🎉</div> : (
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]"><tr><th className="text-left">Party</th><th className="text-right">Amount</th><th className="text-right">Bills</th><th className="text-right">Oldest</th></tr></thead>
                <tbody>
                  {snap.receivables.top_overdue.slice(0, 10).map((p, i) => (
                    <tr key={i} className="border-t border-black/5">
                      <td className="py-1 truncate max-w-[200px]">{p.name}</td>
                      <td className="text-right font-semibold">{inr(p.amt)}</td>
                      <td className="text-right">{p.bills}</td>
                      <td className="text-right">{p.oldest}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* 6. Supplier bills due/overdue 7d */}
        <section className="bg-white border border-black/10 p-5 mb-5" data-testid="snap-supplier-bills">
          <h3 className="font-display text-sm font-bold uppercase font-mono-shelv tracking-widest text-[#717680] mb-3">Supplier bills — overdue or due in 7 days</h3>
          {(snap.payables.due7 || []).length === 0 ? <div className="text-sm text-[#717680] italic">Nothing due in the next 7 days 👍</div> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]"><tr><th className="text-left">Supplier</th><th className="text-left">Bill #</th><th className="text-right">Due</th><th className="text-right">Overdue days</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {snap.payables.due7.slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-t border-black/5">
                    <td className="py-1 truncate max-w-[200px]">{r.name}</td>
                    <td className="font-mono">{r.no || "-"}</td>
                    <td className="text-right">{r.due || "-"}</td>
                    <td className={`text-right ${r.overdue > 0 ? "text-[#D90429] font-bold" : ""}`}>{r.overdue > 0 ? `+${r.overdue}` : `${r.overdue}`}</td>
                    <td className="text-right font-semibold">{inr(r.amt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* 7. Slow stock */}
        <section className="bg-white border border-black/10 p-5" data-testid="snap-slow-stock">
          <h3 className="font-display text-sm font-bold uppercase font-mono-shelv tracking-widest text-[#717680] mb-3">Stock not sold in 90+ days</h3>
          {(snap.stock.slow || []).length === 0 ? <div className="text-sm text-[#717680] italic">All items moved within 90 days.</div> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase font-mono-shelv tracking-widest text-[#717680]"><tr><th className="text-left">Item</th><th className="text-right">Qty</th><th className="text-right">Value</th><th className="text-right">Last sale</th><th className="text-right">Idle</th></tr></thead>
              <tbody>
                {snap.stock.slow.slice(0, 20).map((s, i) => (
                  <tr key={i} className="border-t border-black/5">
                    <td className="py-1 truncate max-w-[260px]">{s.item}</td>
                    <td className="text-right">{s.qty.toLocaleString("en-IN")}</td>
                    <td className="text-right font-semibold">{inr(s.value)}</td>
                    <td className="text-right">{s.last_sale || "never"}</td>
                    <td className="text-right text-[#D90429]">{s.idle_days}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </Layout>
  );
}
